/// Web Speech API wrapper for the NATO drill. Browser support is
/// broad enough on iOS Safari (12+), Chrome, and the Android +
/// iOS Capacitor WebViews that no native plugin is needed. We
/// guard the calls so a non-speech environment (Node tests, very
/// old browsers) silently no-ops.

export interface SpeakOptions {
  /// Speech rate, 0.1 — 10.0. Default 0.9 (just under conversational)
  /// so the listener can pick up the consonants on a "Foxtrot".
  rate?: number;
  /// Pitch, 0 — 2.0. Default 1.0.
  pitch?: number;
  /// Volume, 0 — 1.0. Default 1.0.
  volume?: number;
  /// BCP-47 language tag, e.g. "en-GB". Default unset — uses the
  /// browser's default voice for the user's locale.
  lang?: string;
}

/// Speak the given word using the platform's default voice. Returns
/// a promise that resolves when speech finishes (or immediately if
/// speech isn't available).
export function speak(word: string, options: SpeakOptions = {}): Promise<void> {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) {
    return Promise.resolve();
  }
  // Cancel anything currently speaking so flashcard advances don't
  // stack on top of each other.
  window.speechSynthesis.cancel();

  return new Promise((resolve) => {
    const u = new SpeechSynthesisUtterance(word);
    u.rate = options.rate ?? 0.9;
    u.pitch = options.pitch ?? 1.0;
    u.volume = options.volume ?? 1.0;
    if (options.lang) u.lang = options.lang;
    u.onend = () => resolve();
    u.onerror = () => resolve();
    window.speechSynthesis.speak(u);
  });
}

/// True if the runtime can speak. Use this to hide UI controls in
/// SSR or non-speech browsers.
export function canSpeak(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}
